import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Plus, Trash2, ImagePlus, FileText, X, Loader2, Save } from "lucide-react";
import { STATUS_LABELS } from "@/components/StatusBadge";

const EMPTY_PRODUCT = { room: "", product_type: "", color_model: "", quantity: 1 };

export default function NewCase() {
    const navigate = useNavigate();
    const [meta, setMeta] = useState(null);
    const [users, setUsers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [images, setImages] = useState([]);
    const [receipt, setReceipt] = useState(null);
    const galleryRef = useRef(null);
    const receiptRef = useRef(null);

    const [form, setForm] = useState({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        customer_address: "",
        customer_postal: "",
        customer_city: "",
        order_number: "",
        products: [{ ...EMPTY_PRODUCT }],
        problem_category: "",
        problem_description: "",
        wished_solution: "",
        initial_status: "ny",
        assigned_to: "",
        supplier_id: "",
        initial_note: "",
        send_customer_confirmation: true,
    });

    useEffect(() => {
        Promise.all([
            api.get("/meta/internal"),
            api.get("/users"),
            api.get("/suppliers"),
        ]).then(([m, u, s]) => {
            setMeta(m.data);
            setUsers(u.data);
            setSuppliers(s.data);
        });
    }, []);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const updateProduct = (i, k, v) =>
        setForm((p) => ({
            ...p,
            products: p.products.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)),
        }));
    const addProduct = () => setForm((p) => ({ ...p, products: [...p.products, { ...EMPTY_PRODUCT }] }));
    const removeProduct = (i) =>
        setForm((p) => ({
            ...p,
            products: p.products.length > 1 ? p.products.filter((_, idx) => idx !== i) : p.products,
        }));

    const upload = async (fileList, isReceipt = false) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        setUploading(true);
        try {
            for (const f of files) {
                const ext = f.name.split(".").pop()?.toLowerCase() || "";
                if (!["jpg", "jpeg", "png", "heic", "heif", "webp", "pdf"].includes(ext)) {
                    toast.error(`${f.name}: filtype ikke tilladt`);
                    continue;
                }
                const fd = new FormData();
                fd.append("file", f);
                try {
                    const { data } = await api.post("/agent/upload", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    const rec = { ...data, localUrl: URL.createObjectURL(f) };
                    if (isReceipt) setReceipt(rec);
                    else setImages((p) => [...p, rec]);
                } catch (e) {
                    toast.error(`${f.name}: ${e.response?.data?.detail || "upload fejlede"}`);
                }
            }
        } finally {
            setUploading(false);
        }
    };

    const submit = async () => {
        if (!form.customer_name.trim() || !form.customer_email.trim() || !form.customer_phone.trim()) {
            toast.error("Udfyld kundens navn, email og telefon");
            return;
        }
        if (!form.products.every((p) => p.product_type.trim())) {
            toast.error("Vælg produkttype for alle produkter");
            return;
        }
        if (!form.problem_category) {
            toast.error("Vælg problemkategori");
            return;
        }
        if (!form.problem_description.trim()) {
            toast.error("Beskriv problemet");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                assigned_to: form.assigned_to || null,
                supplier_id: form.supplier_id || null,
                file_ids: images.map((i) => i.id),
                receipt_file_id: receipt?.id || null,
            };
            const { data } = await api.post("/complaints", payload);
            toast.success(`Sag ${data.case_number} oprettet`);
            navigate(`/sag/${data.id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke oprette sag");
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6 max-w-4xl" data-testid="new-case-page">
            <div className="flex items-center gap-4">
                <Link to="/dashboard">
                    <Button variant="ghost" size="sm" className="text-[#5C5854]" data-testid="back-button">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Tilbage
                    </Button>
                </Link>
                <div>
                    <h1 className="font-heading text-3xl font-medium text-[#2C2A29]">Ny reklamation</h1>
                    <p className="text-sm text-[#78736E] mt-1">Opret en sag på vegne af en kunde</p>
                </div>
            </div>

            <Section title="Kundeoplysninger">
                <Grid>
                    <Field label="Navn" required>
                        <Input value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} data-testid="agent-customer-name" placeholder="Anne Jensen" />
                    </Field>
                    <Field label="Email" required>
                        <Input type="email" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} data-testid="agent-customer-email" placeholder="kunde@email.dk" />
                    </Field>
                    <Field label="Telefon" required>
                        <Input value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} data-testid="agent-customer-phone" placeholder="+45 12 34 56 78" />
                    </Field>
                    <Field label="Ordrenummer / faktura">
                        <Input value={form.order_number} onChange={(e) => update("order_number", e.target.value)} data-testid="agent-order-number" />
                    </Field>
                    <Field label="Adresse">
                        <Input value={form.customer_address} onChange={(e) => update("customer_address", e.target.value)} data-testid="agent-customer-address" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Postnr.">
                            <Input value={form.customer_postal} onChange={(e) => update("customer_postal", e.target.value)} data-testid="agent-customer-postal" />
                        </Field>
                        <Field label="By">
                            <Input value={form.customer_city} onChange={(e) => update("customer_city", e.target.value)} data-testid="agent-customer-city" />
                        </Field>
                    </div>
                </Grid>
            </Section>

            <Section title="Produkt(er)">
                <div className="space-y-3">
                    {form.products.map((p, i) => (
                        <div key={i} className="border border-[#E5E1DA] rounded-lg p-4 bg-[#FAFAF9]">
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Produkt {i + 1}</div>
                                {form.products.length > 1 && (
                                    <Button variant="ghost" size="sm" onClick={() => removeProduct(i)} className="text-[#993C3C] hover:bg-red-50" data-testid={`agent-remove-product-${i}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                )}
                            </div>
                            <Grid>
                                <Field label="Rum">
                                    <Input value={p.room} onChange={(e) => updateProduct(i, "room", e.target.value)} placeholder="f.eks. Stue" data-testid={`agent-product-room-${i}`} />
                                </Field>
                                <Field label="Produkttype" required>
                                    <Select value={p.product_type} onValueChange={(v) => updateProduct(i, "product_type", v)}>
                                        <SelectTrigger data-testid={`agent-product-type-${i}`}>
                                            <SelectValue placeholder="Vælg type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {(meta?.product_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field label="Farve / model">
                                    <Input value={p.color_model} onChange={(e) => updateProduct(i, "color_model", e.target.value)} data-testid={`agent-product-color-${i}`} />
                                </Field>
                                <Field label="Antal">
                                    <Input type="number" min={1} value={p.quantity} onChange={(e) => updateProduct(i, "quantity", Math.max(1, parseInt(e.target.value || "1")))} data-testid={`agent-product-qty-${i}`} />
                                </Field>
                            </Grid>
                        </div>
                    ))}
                    <Button variant="outline" onClick={addProduct} className="w-full border-dashed border-[#D2C9B9] bg-transparent hover:bg-[#F0EBE1]" data-testid="agent-add-product">
                        <Plus className="w-4 h-4 mr-2" />Tilføj produkt
                    </Button>
                </div>
            </Section>

            <Section title="Problem">
                <div className="space-y-4">
                    <Field label="Kategori" required>
                        <Select value={form.problem_category} onValueChange={(v) => update("problem_category", v)}>
                            <SelectTrigger data-testid="agent-problem-category"><SelectValue placeholder="Vælg kategori" /></SelectTrigger>
                            <SelectContent>
                                {(meta?.problem_categories || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Beskrivelse" required>
                        <Textarea rows={5} value={form.problem_description} onChange={(e) => update("problem_description", e.target.value)} data-testid="agent-problem-description" placeholder="Hvad fortalte kunden? Beskriv problemet så præcist som muligt..." />
                    </Field>
                    <Field label="Ønsket løsning">
                        <Select value={form.wished_solution} onValueChange={(v) => update("wished_solution", v)}>
                            <SelectTrigger data-testid="agent-wished-solution"><SelectValue placeholder="Vælg løsning" /></SelectTrigger>
                            <SelectContent>
                                {(meta?.wish_options || []).map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                </div>
            </Section>

            <Section title="Billeder og kvittering (valgfri)">
                <div className="text-xs text-[#78736E] -mt-2 mb-3">
                    Kunden kan også tilføje billeder selv via deres samtalelink efter sagen er oprettet
                </div>
                <input ref={galleryRef} type="file" accept="image/*,.heic,.heif,application/pdf" multiple className="hidden"
                    onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
                    data-testid="agent-gallery-input" />
                <input ref={receiptRef} type="file" accept="image/*,.heic,.heif,application/pdf" className="hidden"
                    onChange={(e) => { upload(e.target.files, true); e.target.value = ""; }}
                    data-testid="agent-receipt-input" />

                {images.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
                        {images.map((img) => (
                            <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden border border-[#E5E1DA] bg-[#F4F2EF]">
                                {img.ext === "pdf" ? (
                                    <div className="w-full h-full flex flex-col items-center justify-center p-1 text-center">
                                        <FileText className="w-5 h-5 text-[#78736E]" />
                                        <div className="text-[9px] text-[#78736E] truncate w-full mt-1">{img.filename}</div>
                                    </div>
                                ) : (
                                    <img src={img.localUrl} alt="" className="w-full h-full object-cover" />
                                )}
                                <button
                                    onClick={() => setImages((p) => p.filter((x) => x.id !== img.id))}
                                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center"
                                    data-testid={`agent-remove-image-${img.id}`}
                                ><X className="w-3 h-3" /></button>
                            </div>
                        ))}
                    </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                    <Button variant="outline" onClick={() => galleryRef.current?.click()} disabled={uploading} className="border-[#D2C9B9] hover:bg-[#F0EBE1]" data-testid="agent-upload-images">
                        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-2" />}
                        Upload billeder
                    </Button>
                    <Button variant="outline" onClick={() => receiptRef.current?.click()} disabled={uploading} className="border-[#D2C9B9] hover:bg-[#F0EBE1]" data-testid="agent-upload-receipt">
                        <FileText className="w-4 h-4 mr-2" />
                        {receipt ? `Kvittering: ${receipt.filename.slice(0, 20)}` : "Upload kvittering"}
                    </Button>
                </div>
            </Section>

            <Section title="Behandling">
                <Grid>
                    <Field label="Startstatus">
                        <Select value={form.initial_status} onValueChange={(v) => update("initial_status", v)}>
                            <SelectTrigger data-testid="agent-initial-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Tildel til">
                        <Select value={form.assigned_to || "none"} onValueChange={(v) => update("assigned_to", v === "none" ? "" : v)}>
                            <SelectTrigger data-testid="agent-assign-to"><SelectValue placeholder="Ingen" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">Ikke tildelt</SelectItem>
                                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Leverandør">
                        <Select value={form.supplier_id || "none"} onValueChange={(v) => update("supplier_id", v === "none" ? "" : v)}>
                            <SelectTrigger data-testid="agent-supplier"><SelectValue placeholder="Ingen" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">Ingen leverandør</SelectItem>
                                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                </Grid>
                <div className="mt-4">
                    <Field label="Intern note (valgfri)">
                        <Textarea rows={2} value={form.initial_note} onChange={(e) => update("initial_note", e.target.value)} data-testid="agent-initial-note" placeholder="f.eks. info fra telefonsamtale" />
                    </Field>
                </div>
            </Section>

            <div className="bg-white border border-[#E5E1DA] rounded-xl p-5">
                <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                        checked={form.send_customer_confirmation}
                        onCheckedChange={(v) => update("send_customer_confirmation", !!v)}
                        data-testid="agent-send-confirmation"
                        className="mt-0.5"
                    />
                    <div>
                        <div className="text-sm font-medium text-[#2C2A29]">Send bekræftelses-mail til kunden</div>
                        <div className="text-xs text-[#78736E] mt-0.5">Kunden modtager sagsnummer og link til at følge sagen og sende beskeder</div>
                    </div>
                </label>
            </div>

            <Separator className="bg-[#E5E1DA]" />

            <div className="flex items-center justify-end gap-3">
                <Link to="/dashboard">
                    <Button variant="ghost">Annuller</Button>
                </Link>
                <Button onClick={submit} disabled={submitting} className="bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="agent-submit-button">
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Opret sag
                </Button>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="bg-white border border-[#E5E1DA] rounded-xl p-6">
            <h2 className="font-heading text-base font-medium text-[#2C2A29] mb-4">{title}</h2>
            {children}
        </div>
    );
}

function Grid({ children }) {
    return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-sm font-medium text-[#2C2A29]">
                {label} {required && <span className="text-[#993C3C]">*</span>}
            </Label>
            {children}
        </div>
    );
}
